// components/ui/DropdownSelect.tsx
import React, { useState, useRef, useEffect } from 'react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DropdownSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
  minWidth?: number;
  maxWidth?: number;
}

const DropdownSelect = ({
  value,
  onValueChange,
  options,
  placeholder = "Select option",
  disabled = false,
  className,
  triggerClassName,
  minWidth = 120,
  maxWidth = 300
}: DropdownSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentWidth, setContentWidth] = useState<number>(minWidth);
  console.log("DD")
  // Calculate optimal width based on content
  useEffect(() => {
    if (!isOpen) return;

    const calculateOptimalWidth = () => {
      const triggerWidth = triggerRef.current?.offsetWidth || minWidth;

      const optionTexts = options.map(opt => opt.label);
      const placeholderText = placeholder;
      const allTexts = [...optionTexts, placeholderText, value];

      const tempSpan = document.createElement('span');
      tempSpan.style.visibility = 'hidden';
      tempSpan.style.position = 'absolute';
      tempSpan.style.whiteSpace = 'nowrap';
      tempSpan.style.fontSize = '14px';
      tempSpan.style.fontFamily = 'inherit';
      document.body.appendChild(tempSpan);

      let maxTextWidth = minWidth;
      allTexts.forEach(text => {
        tempSpan.textContent = text;
        const textWidth = tempSpan.offsetWidth + 60;
        if (textWidth > maxTextWidth) {
          maxTextWidth = Math.min(textWidth, maxWidth);
        }
      });

      document.body.removeChild(tempSpan);

      const optimalWidth = Math.max(
        minWidth,
        Math.min(maxWidth, Math.max(triggerWidth, maxTextWidth))
      );

      setContentWidth(optimalWidth);
    };

    const timer = setTimeout(calculateOptimalWidth, 10);
    return () => clearTimeout(timer);
  }, [isOpen, options, placeholder, value, minWidth, maxWidth]);

  const selectedOption = options.find(opt => opt.value === value);
  const displayValue = selectedOption ? selectedOption.label : placeholder;

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger
        ref={triggerRef}
        disabled={disabled}
        style={{ minWidth: `${minWidth}px` }}
        className={cn(
          "flex items-center justify-between border border-gray-600 rounded px-3 py-2 bg-white text-sm",
          "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          "transition-colors hover:border-gray-400",
          "text-left",
          triggerClassName
        )}
      >
        <span className="truncate flex-1">{displayValue}</span>
        <ChevronDown className={cn(
          "w-4 h-4 flex-shrink-0 ml-2 transition-transform",
          isOpen && "transform rotate-180"
        )} />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        ref={contentRef}
        style={{ width: contentWidth, minWidth: minWidth }}
        className={cn(
          "max-h-60 overflow-y-auto z-50 p-1",
          "border border-gray-200 bg-white rounded-md shadow-lg",
          className
        )}
        align="start"
        avoidCollisions={false}
      >
        {options.map((option) => (
          <DropdownMenuItem
            key={option.value}
            onSelect={() => {
              onValueChange(option.value);
              setIsOpen(false);
            }}
            className={cn(
              "cursor-pointer text-sm px-3 py-2 rounded-sm",
              "hover:bg-gray-100 focus:bg-gray-100",
              "transition-colors",
              value === option.value && "bg-blue-50 text-blue-700 hover:bg-blue-100"
            )}
          >
            <span className="truncate">{option.label}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default DropdownSelect;